import { computed, makeObservable } from 'mobx'
import { MeetingResultsSlide } from './MeetingResultsSlide'
import { sortQuestions } from '../../../sources-of-truth/sortQuestions'
import { StudentPeerReviewController } from '../../StudentAssignment/StudentPeerReviewController'

type EndOfSessionQuizPhase =
  | 'loading'
  | 'quiz'
  | 'peerReview'
  | 'waiting'
  | 'processing'
  | 'results'

export class EndOfSessionQuizSlide extends MeetingResultsSlide {
  private peerReviewController?: StudentPeerReviewController

  afterConstructor() {
    makeObservable(this, {
      questions: computed,
      phase: computed,
    })
  }

  // True when this room will get a peer-review controller but the assignment it
  // needs has not arrived yet. The controller snapshots
  // peerReviewExcludesSelfReview at construction and is then memoized for the
  // life of the slide, so building it early freezes the flag on its `?? false`
  // fallback: the student gets a self-review row the assignment says to
  // exclude, nothing can fill it in, and Submit stays disabled forever.
  //
  // The room-state conditions match getPeerReviewController's. A room with no
  // assignment never gets a controller, and nothing ever fetches its assignment
  // (see MeetingDataFetcher.getRoomStateDependentData), so its assignment model
  // stays unloaded forever — such rooms must not wait on it or the slide hangs
  // on the loading spinner.
  private get peerReviewControllerIsPending(): boolean {
    const roomState = this.meeting.roomState
    if (
      !roomState.isLoaded ||
      !roomState.data.sectionId ||
      !roomState.data.assignmentId
    ) {
      return false
    }

    return this.meeting.assignment.isLoading
  }

  private getPeerReviewController(): StudentPeerReviewController | undefined {
    const roomState = this.meeting.roomState
    if (
      !roomState.isLoaded ||
      !roomState.data.sectionId ||
      !roomState.data.assignmentId
    ) {
      return undefined
    }

    if (this.peerReviewControllerIsPending) return undefined

    if (!this.peerReviewController) {
      this.peerReviewController = new StudentPeerReviewController(
        this.meeting.repository,
        {
          roomId: roomState.id,
          userId: this.meeting.repository.uid,
          sectionId: roomState.data.sectionId,
          assignmentId: roomState.data.assignmentId,
          peerReviewExcludesSelfReview:
            this.meeting.assignment.data.peerReviewExcludesSelfReview ?? false,
          userIdsInRoom: roomState.userIds,
        }
      )
      this.peerReviewController.initializeAsReviewer()
    }

    return this.peerReviewController
  }

  get questions() {
    // Filter by this slide's own stable id, not `currentSlide?.id`. On a Safari
    // refresh the LiveKit reconnect churns re-renders while `currentSlide` is
    // momentarily undefined; with the old filter `questions` went transiently
    // empty, and an empty list makes `phase`'s `every()` vacuously true — the
    // slide then advanced past the quiz to processing/results, blanking the
    // panel ("questions disappeared"). `this.id` is set once at construction
    // (see Slide base, which filters its own question helpers the same way), so
    // it does not flicker. When this slide is rendered it *is* the current
    // slide, so the two ids are otherwise identical.
    const endOfSessionQuestions = this.meeting.questions.filter(
      (q) => q.slideId === this.id && q.isQuiz
    )
    return sortQuestions({ questions: endOfSessionQuestions, slideIds: [] })
  }

  get titleIndex(): number {
    // loop over currentSlide.questions and find out if all are submitted
    for (const q of this.questions) {
      if (!q.isSubmitted) {
        return 0
      }
    }
    return 1
  }

  get canProceed() {
    return (
      this.meeting.endOfSessionQuizSlideCanProceed || this.meeting.hasFeedback
    )
  }

  get peerReviewControllerForView(): StudentPeerReviewController | undefined {
    return this.getPeerReviewController()
  }

  get hasQuizOrPeerReview(): boolean {
    return (
      this.questions.length > 0 ||
      this.peerReviewControllerForView?.peerReviewsEnabled.value === true
    )
  }

  get phase(): EndOfSessionQuizPhase {
    if (
      this.meeting.slideDeckQuestions.isLoading ||
      // Without this the slide reads the withheld controller as "no peer
      // review" and falls through to processing/results instead of the form.
      this.peerReviewControllerIsPending ||
      this.peerReviewControllerForView?.userYetToCompletePeerReview.isLoading ||
      // hasQuizOrPeerReview reads peerReviewsEnabled to pick the proceed
      // button's label; waiting for it here stops that label being rendered as
      // "Show Results" and then flipping to "Next Slide" once it resolves
      this.peerReviewControllerForView?.peerReviewsEnabled.isLoading
    )
      return 'loading'

    const allAnswered = this.questions.every(
      (question) => question.isAnswered && question.isSubmitted
    )

    if (this.questions.length > 0 && !allAnswered) return 'quiz'

    if (this.questions.length === 0 || allAnswered) {
      if (this.peerReviewControllerForView?.userYetToCompletePeerReview.value) {
        return 'peerReview'
      }

      // Whether to hold this finisher on the waiting screen is decided by room
      // *membership*, not live *presence*. A peer who refreshes, drops, or
      // rejoins stays a room member, so keying off membership stops a presence
      // blip from flipping a waiting finisher to processing and back (BL-4691),
      // while a genuinely solo room still skips the wait:
      //
      //  - Solo room (one member): no group to wait for — fall through to the
      //    auto-proceed path and advance to processing, as before.
      //  - Group room (2+ members): stay on the waiting screen until the group
      //    reconvenes, regardless of who is momentarily present. The waiting
      //    screen's own "Pending" count reflects present-and-unfinished peers
      //    and may read 0 (with an active proceed button); the durable reconvene
      //    signal then moves the finisher to processing exactly once.
      //  - Genuine last finisher (a peer present AND everyone present finished):
      //    the group auto-reconvenes, so skip waiting rather than flash it while
      //    the shared signal round-trips. Presence is required here so a peer
      //    merely leaving cannot satisfy the "everyone finished" shortcut.
      const isSoloRoom = this.meeting.roomState.userCount <= 1
      const everyonePresentHasFinished =
        this.meeting.presentUserIds.length >= 1 &&
        this.meeting.endOfSessionAllPresentFinishedQuiz
      const waitingApplies =
        this.questions.length > 0 && !isSoloRoom && !everyonePresentHasFinished
      if (waitingApplies && !this.meeting.endOfSessionReconvened)
        return 'waiting'

      if (this.meeting.endOfSessionQuizSlideShouldProceed) return 'results'
      // if someone has proceeded, go to results, otherwise go to processing
      return 'processing'
    }

    return 'results'
  }
}
